import axios from 'axios'
import jsforce from 'jsforce'
import { devLog } from 'utilsTS'
import { mapRoles } from '../../auth/authRoles'
import localStorageService from '../localStorageService'
import sfOauthConfig from './sfAuthConfig'
import { getAccountParsedById } from './sfData/sfAccount'
import {
  appSetDefaultAccountTeamThroughFlow,
  getUserGroupMembership,
  getUserParsed,
  saveUser
} from './sfData/sfUser'
import { getConfigurationVariablesParsed } from './sfData/sfVariables'

export const NO_USER = 'no_user'
export const NO_ID_PROVIDED = 'no id for record was provided'

class SFAuthService {
  auth
  jsForceOauth
  user = {
    role: 'GRANTEE',
    token: null
  }

  constructor () {
    this.init()
  }

  init = () => {
    this.jsForceOauth = new jsforce.OAuth2(sfOauthConfig.oauth2)
  }

  setUser = (user) => {
    devLog('sfAuthService set user', user)
    this.user = user
    localStorageService.setItem('auth_user', user)
  }

  removeUser = () => {
    this.user = null
    localStorage.removeItem('auth_user')
  }

  getConnection = (user) => {
    if (!user) {
      user = this.user
    }
    if (user) {
      const conn = new jsforce.Connection({
        oauth2: sfOauthConfig.oauth2,
        // logLevel: 'DEBUG',
        instanceUrl: sfOauthConfig.instanceUrl,
        accessToken: user.access_token,
        // refreshToken: user.refresh_token,
        version: '49.0'
      })
      return conn
    } else {
      return null
    }
  }

  doLogin = (state) => {
    window.location = this.jsForceOauth.getAuthorizationUrl({
      response_type: sfOauthConfig.response_type,
      scope: sfOauthConfig.scope,
      state: JSON.stringify(state)
    })
  }

  parseToken = () => {
    const query = new URLSearchParams(window.location.query)
    // http://localhost:3000/?
    // error=OAUTH_APP_ACCESS_DENIED&
    // error_description=user+is+not+admin+approved+to+access+this+app
    // &state=%7B%22uri%22%3A%22%2F%22%7D

    if (query.error === 'OAUTH_APP_ACCESS_DENIED') {
      console.error(query.error_description)
      return Promise.reject(query.error)
    }
    // const params = new URLSearchParams(window.location.hash.slice(1))
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    console.log('authorization code', code)
    // const refresh_token = params.get('refresh_token')
    const state = JSON.parse(params.get('state'))
    const tokenUrl =  `${sfOauthConfig.instanceUrl}/services/oauth2/token`
    const postData = new URLSearchParams(Object.entries({
      code: code,
      grant_type: 'authorization_code',
      client_id: sfOauthConfig.oauth2.clientId,
      redirect_uri: sfOauthConfig.oauth2.redirectUri,
    }))
    return axios.post(tokenUrl, postData).then(response => {
      console.log('token response', response)
      if (response.status !== 200) {
        console.error('Did not get token back')
        return Promise.reject(NO_USER)
      }
      const id = response.data.id
      const access_token = response.data.access_token
    // console.log(
    //   'oauth2 redirect data',
    //   id,
    //   Object.fromEntries(params),
    //   access_token,
    //   refresh_token
    // )

      const userId = id ? id.split('/')[5] : ''
    /*
    https://test.salesforce.com/id/00D4m0000008bIYEAY/0054m000000KIPHAA4

    access_token=0
    instance_url=https%3A%2F%2Fchtc-ctlc--pc.my.salesforce.com
    id=https%3A%2F%2Ftest.salesforce.com%2Fid%2F00D4c0000008cutEAA%2F0054c000000JlQbAAK
    issued_at=1590558555169
    signature=j5AdKFvbTdWxY%2BiFIVvs88t0tRt28tV3Z1Q0FsEGB3s%3D
    sfdc_community_url=https%3A%2F%2Fpc-chtc.cs138.force.com%2FAPIConnectionPortal
    sfdc_community_id=0DB4c00000000OwGAI
    state=%7B%22uri%2/2%3A%22%2F%22%7D
    scope=id+api+web
    token_type=Bearer
    */
      if (access_token) {
        const user = {
          id,
          userId,
          access_token
          // refresh_token
        }
        this.setUser(user)
        return this.getUserInfo(user, state)
      } else {
        this.setUser(null)
        return Promise.reject(NO_USER)
      }
    })
  }

  getUserInfo = async (user, state = {}) => {
    try {
      const userInfoResponse = await axios.get(user.id, {
        params: { access_token: user.access_token }
      })
      const userInfo = userInfoResponse.data

      const [userObject, variables, groupMemberships] = await Promise.all([
        getUserParsed(user.userId, true),
        getConfigurationVariablesParsed(),
        getUserGroupMembership(user.userId)
      ])

      const retObject = {
        configuration: variables,
        user: {
          ...user,
          groupMemberships,
          organizationMember: null,
          role: mapRoles(userObject.role),
          email: userInfo.email,
          displayName: userInfo.display_name,
          firstName: userInfo.first_name,
          lastName: userInfo.last_name,
          photoURL: userInfo.photos.picture,
          thumbnailURL: userInfo.photos.thumbnail,
          userId: userInfo.user_id,
          username: userInfo.username,
          language: userObject.language,
          profile: userObject.profile,
          userInfo,
          userObject,
          userFilled: true
        },
        organization: {},
        state
      }

      let orgId = userObject.associatedOrganizations

      if (!orgId) {
        const result = await appSetDefaultAccountTeamThroughFlow(
          userInfo.user_id
        )
        orgId = result[0].outputValues.result
        userObject.associatedOrganizations = orgId
      }

      if (!orgId) {
        return retObject
      }

      const acc = await getAccountParsedById(orgId, {
        permissions: { TEAM_MEMBERS: true }
      })

      const noValidAccount =
        !acc || !acc.members.find((m) => m.UserId === user.userId)

      if (noValidAccount) {
        await saveUser({
          Id: user.userId,
          Associated_Organizations__c: null
        })

        const result = await appSetDefaultAccountTeamThroughFlow(
          userInfo.user_id
        )
        const newOrgId = result[0].outputValues.result
        const newAcc = await getAccountParsedById(newOrgId, {
          permissions: { TEAM_MEMBERS: true }
        })
        retObject.organization = { ...newAcc }
        retObject.user.organizationMember = newAcc.members.find(
          (m) => m.UserId === user.userId
        )
        return retObject
      }

      retObject.organization = { ...acc }
      retObject.user.organizationMember = acc.members.find(
        (m) => m.UserId === user.userId
      )

      return retObject
    } catch (error) {
      if (error.message === 'Network Error' || error.message.includes('expired access/refresh token')) {
        throw error
      }
      // retry po 5s
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await this.getUserInfo(user, state)
          resolve(result)
        }, 5000)
      })
    }
  }

  checkLogin = (user) => {
    return this.getUserInfo(user)
  }

  logout = () => {
    const conn = this.getConnection()
    if (!conn) {
      return Promise.reject()
    }
    return conn.logout().then(
      (res) => {
        devLog('logged out user', res)
        this.setUser(null)
      },
      (reject) => {
        this.setUser(null)
      }
    )
  }
}

const instance = new SFAuthService()

export default instance
